import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { apiOkr } from 'app/config/axios-interceptor';
import { IKr, defaultValue } from 'app/shared/model/kr.model';
import { EntityState, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<IKr> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  adjustmentMidYearQuantitative: {},
  loadingAdjustmentMidYearQuantitative: false,
};

const apiUrl = 'api/okr/key-result';

// Actions

export const createEntity = createAsyncThunk(
  'key-result-quantitative/create_entity',
  async (entity: IKr, thunkAPI) => {
    const result = await apiOkr.post<IKr>(`${apiUrl}/quantitative`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const createCopyEntity = createAsyncThunk(
  'key-result-quantitative/create_copy_entity',
  async (entity: IKr, thunkAPI) => {
    const result = await apiOkr.post<IKr>(`${apiUrl}/quantitative-ver2`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const createKrQuanti = createAsyncThunk(
  'key-result-quantitative/create_kr_quanti',
  async (entity: IKr, thunkAPI) => {
    const result = await apiOkr.post<IKr>(`${apiUrl}/quantitative`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'key-result-quantitative/update_entity',
  async (entity: IKr, thunkAPI) => {
    const result = await apiOkr.put<IKr>(`${apiUrl}/quantitative`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateCopyEntity = createAsyncThunk(
  'key-result-quantitative/update_copy_entity',
  async (entity: IKr, thunkAPI) => {
    const result = await apiOkr.put<IKr>(`${apiUrl}/year-planning/adjustment-mid-year-quantitative`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateCopyEntityVer2 = createAsyncThunk(
  'key-result-quantitative/update_copy_entity_ver2',
  async (entity: IKr, thunkAPI) => {
    const result = await apiOkr.put<IKr>(`${apiUrl}/quantitative-ver2`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);
export const getAdjustmentMidYearQuantitative = createAsyncThunk(
  'key-result-quantitative/get_adjustment_mid_year_quantitative',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.get<any>('/api/okr/year-planning/adjustment-mid-year-quantitative', entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const getAdjustmentMidYearQuantitativeByKrId = createAsyncThunk(
  'key-result-quantitative/get_adjustment_mid_year_quantitative_by_kr_id',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.get<any>(`/api/okr/year-planning/adjustment-mid-year-quantitative?krId=${entity.krId}&planPeriodId=${entity.planPeriodId}`);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const createAdjustmentMidYearQuantitative = createAsyncThunk(
  'key-result-quantitative/create_adjustment_mid_year_quantitative',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>('/api/okr/year-planning/adjustment-mid-year-quantitative', entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

type IDetailKr = {
  idKrBaseSelected?: string | number;
  planPeriodId?: string | number;
  okrVersion?: number;
};
export const getEntity = createAsyncThunk(
  'key-result/fetch_entity_quantitative',
  async ({ planPeriodId, idKrBaseSelected }: IDetailKr) => {
    const requestUrl = `${apiUrl}/quantitative-ver2?krId=${idKrBaseSelected}&planPeriodId=${planPeriodId}`;
    return apiOkr.get<IKr>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getCopyEntity = createAsyncThunk(
  'key-result/fetch_copy_entity_quantitative',
  async ({ planPeriodId, idKrBaseSelected, okrVersion }: IDetailKr) => {
    const requestUrl = `${apiUrl}/kr-quan-copy-this`;
    return apiOkr.get<IKr>(requestUrl, {
      params: { krId: idKrBaseSelected, okrVersion, planPeriodId },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getCopyEntityNextPlanPeriod = createAsyncThunk(
  'key-result/fetch_copy_entity_quantitative_next_plan_period',
  async ({ planPeriodId, idKrBaseSelected, okrVersion }: IDetailKr) => {
    const requestUrl = `${apiUrl}/kr-quan-copy-this-to-next-year`;
    return apiOkr.get<IKr>(requestUrl, {
      params: { krId: idKrBaseSelected, okrVersion, planPeriodId },
    });
  },
  { serializeError: serializeAxiosError },
);

export const checkInKrQuantiAdjust = createAsyncThunk(
  'key-result-quantitative/key_result_quanti_adjustment_mid_year',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/adjustment-mid-year-quantitative`, entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const checkInKrQuantiHalfMonthAdjust = createAsyncThunk(
  'key-result-quantitative/key_result_quanti_adjustment_half_month',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/adjustment-half-month-quantitative`, entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const checkInKrQuantiMonthAdjust = createAsyncThunk(
  'key-result-quantitative/key_result_quanti_adjustment_month',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/adjustment-month-quantitative`, entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const KrQuantitativeSlice = createEntitySlice({
  name: 'key-result-quantitative',
  initialState,
  reducers: {
    resetKrQuantitative(state) {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getCopyEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getCopyEntityNextPlanPeriod.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(createEntity, createCopyEntity, updateEntity, updateCopyEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntity, getCopyEntity, getCopyEntityNextPlanPeriod), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entity = {};
      })
      .addMatcher(isPending(createEntity, createCopyEntity, updateEntity, updateCopyEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isFulfilled(getAdjustmentMidYearQuantitativeByKrId), (state, action) => {
        state.loadingAdjustmentMidYearQuantitative = false;
        state.adjustmentMidYearQuantitative = action.payload.data;
      })
      .addMatcher(isPending(getAdjustmentMidYearQuantitativeByKrId), state => {
        state.loadingAdjustmentMidYearQuantitative = true;
      });
    
  },
});

export const { reset, resetKrQuantitative } = KrQuantitativeSlice.actions;

// Reducer
export default KrQuantitativeSlice.reducer;
